import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import Waveform from "@/components/Waveform";

export default function AdminLogin() {
  const { user, login, logout } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && user.role !== "admin") {
      setError("This account is not authorized for admin access.");
      logout();
    }
  }, [user, logout]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    const res = await login(email, password, remember);
    if (!res.ok) setError(res.error || "Login failed.");
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center px-6" data-testid="admin-login-page">
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
        className="w-full max-w-sm">
        <Logo variant="full" size="md" asLink={false} />
        <p className="eyebrow mt-8 mb-2">Restricted area</p>
        <h1 className="font-anton uppercase text-4xl">Admin Access</h1>
        <Waveform color="rgb(var(--graphite))" className="my-8" />
        <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
          <div>
            <label className="eyebrow block mb-2">Email</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email"
              className="w-full bg-transparent border-b border-border py-3 font-mono text-sm focus:border-pulse outline-none" />
          </div>
          <div>
            <label className="eyebrow block mb-2">Password</label>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password"
              className="w-full bg-transparent border-b border-border py-3 font-mono text-sm focus:border-pulse outline-none" />
          </div>
          <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="admin-login-remember-label">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
              data-testid="admin-login-remember-checkbox"
              className="h-4 w-4 cursor-pointer" style={{ accentColor: "rgb(var(--signal))" }} />
            <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-graphite">
              Stay connected for 30 days
            </span>
          </label>
          {error && <p className="text-destructive font-mono text-xs" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-submit" className="btn-pulse w-full disabled:opacity-60">
            {loading ? "Verifying…" : "Enter Dashboard"}
          </button>
        </form>
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-graphite mt-8">
          Authorized administrators only. All access is logged.
        </p>
      </motion.div>
    </div>
  );
}
